/**
 * Frontend Configuration Module
 *
 * Provides typed, validated access to application configuration.
 * Configuration is injected by Rails via window.__CONFIG__
 *
 * @example
 * import { config } from '@/lib/config';
 *
 * config.app.name;           // "aMortal"
 * config.sentry.dsn;         // "https://..."
 * config.stripe.publicKey;   // "pk_test_..."
 */

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────
// Schema Definitions
// ─────────────────────────────────────────────────────────────

/**
 * Application configuration schema
 */
const appConfigSchema = z.object({
  name: z.string().default('aMortal'),
  version: z.string().default('0.1.0'),
  environment: z
    .enum(['development', 'test', 'production'])
    .default('development'),
});

/**
 * Sentry error tracking configuration schema
 */
const sentryConfigSchema = z.object({
  dsn: z.string().nullable().optional(),
  environment: z.string().default('development'),
  enabled: z.boolean().default(false),
  tracesSampleRate: z.number().min(0).max(1).default(0),
});

/**
 * Analytics configuration schema
 */
const analyticsConfigSchema = z.object({
  gaMeasurementId: z.string().nullable().optional(),
  firebase: z
    .object({
      apiKey: z.string().nullable().optional(),
      appId: z.string().nullable().optional(),
      authDomain: z.string().nullable().optional(),
      measurementId: z.string().nullable().optional(),
      projectId: z.string().nullable().optional(),
    })
    .default({}),
});

/**
 * OAuth provider schema
 */
const oauthProviderSchema = z.object({
  key: z.string(),
  name: z.string(),
});

/**
 * OAuth configuration schema
 */
const oauthConfigSchema = z.object({
  providers: z.array(oauthProviderSchema).default([]),
});

/**
 * Stripe configuration schema
 */
const stripeConfigSchema = z.object({
  publicKey: z.string().nullable().optional(),
});

const revenueCatConfigSchema = z.object({
  iosApiKey: z.string().nullable().optional(),
  androidApiKey: z.string().nullable().optional(),
  products: z.record(z.string(), z.enum(['pro', 'family'])).default({}),
  termsUrl: z.string().url().nullable().optional(),
  privacyUrl: z.string().url().nullable().optional(),
});

/**
 * Root configuration schema
 */
const configSchema = z.object({
  app: appConfigSchema.default({}),
  sentry: sentryConfigSchema.default({}),
  analytics: analyticsConfigSchema.default({}),
  oauth: oauthConfigSchema.default({}),
  stripe: stripeConfigSchema.default({}),
  revenuecat: revenueCatConfigSchema.default({}),
});

// ─────────────────────────────────────────────────────────────
// Type Exports
// ─────────────────────────────────────────────────────────────

export type AppConfig = z.infer<typeof appConfigSchema>;
export type SentryConfig = z.infer<typeof sentryConfigSchema>;
export type AnalyticsConfig = z.infer<typeof analyticsConfigSchema>;
export type OAuthProvider = z.infer<typeof oauthProviderSchema>;
export type OAuthConfig = z.infer<typeof oauthConfigSchema>;
export type StripeConfig = z.infer<typeof stripeConfigSchema>;
export type RevenueCatConfig = z.infer<typeof revenueCatConfigSchema>;
export type Config = z.infer<typeof configSchema>;

// ─────────────────────────────────────────────────────────────
// Window Type Declaration
// ─────────────────────────────────────────────────────────────

declare global {
  interface Window {
    __CONFIG__?: Record<string, unknown>;
    // Legacy support - will be deprecated
    __SENTRY_DSN__?: string;
    __RAILS_ENV__?: string;
  }
}

// ─────────────────────────────────────────────────────────────
// Configuration Loading
// ─────────────────────────────────────────────────────────────

/**
 * Load and validate configuration from window.__CONFIG__
 * Falls back to legacy window variables and development defaults
 */
function loadConfig(): Config {
  // Get raw config from window or use empty object
  const browserWindow = globalThis.window;
  const rawConfig = browserWindow?.__CONFIG__ ?? {};

  // Support legacy config format (migrate from window.__SENTRY_DSN__ etc.)
  const legacyConfig = {
    app: {
      environment: browserWindow?.__RAILS_ENV__ ?? 'development',
    },
    sentry: {
      dsn: browserWindow?.__SENTRY_DSN__,
      environment: browserWindow?.__RAILS_ENV__ ?? 'development',
      enabled: browserWindow?.__RAILS_ENV__ === 'production',
    },
  };

  // Merge legacy config with new config (new config takes precedence)
  const mergedConfig = deepMerge(legacyConfig, rawConfig);

  // Validate and return
  const result = configSchema.safeParse(mergedConfig);

  if (!result.success) {
    console.error('Invalid configuration:', result.error.issues);
    // Return defaults on error
    return configSchema.parse({});
  }

  return result.data;
}

/**
 * Deep merge two objects
 */
function deepMerge<T extends Record<string, unknown>>(
  target: T,
  source: Record<string, unknown>,
): T {
  const result = { ...target } as T;

  for (const key of Object.keys(source)) {
    const sourceValue = source[key];
    const targetValue = result[key as keyof T];

    if (
      sourceValue !== null &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue !== null &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      result[key as keyof T] = deepMerge(
        targetValue as Record<string, unknown>,
        sourceValue as Record<string, unknown>,
      ) as T[keyof T];
    } else if (sourceValue !== undefined) {
      result[key as keyof T] = sourceValue as T[keyof T];
    }
  }

  return result;
}

// ─────────────────────────────────────────────────────────────
// Singleton Export
// ─────────────────────────────────────────────────────────────

/**
 * Application configuration singleton
 * Loaded and validated once on module import
 */
export const config: Config = loadConfig();

// ─────────────────────────────────────────────────────────────
// Convenience Exports
// ─────────────────────────────────────────────────────────────

/**
 * Check if running in production environment
 */
export const isProduction = config.app.environment === 'production';

/**
 * Check if running in development environment
 */
export const isDevelopment = config.app.environment === 'development';

/**
 * Check if running in test environment
 */
export const isTest = config.app.environment === 'test';

/**
 * Check if Sentry is enabled
 */
export const isSentryEnabled = config.sentry.enabled && !!config.sentry.dsn;

/**
 * Check if Google Analytics is configured
 */
export const isAnalyticsEnabled = [
  'apiKey',
  'appId',
  'authDomain',
  'measurementId',
  'projectId',
].every((key) => !!config.analytics.firebase?.[key]);

/**
 * Check if Stripe is configured
 */
export const isStripeConfigured = !!config.stripe.publicKey;

/**
 * Get list of enabled OAuth providers
 */
export const enabledOAuthProviders = config.oauth.providers;

// ─────────────────────────────────────────────────────────────
// Schema Exports (for testing/validation)
// ─────────────────────────────────────────────────────────────

export const schemas = {
  app: appConfigSchema,
  sentry: sentryConfigSchema,
  analytics: analyticsConfigSchema,
  oauth: oauthConfigSchema,
  oauthProvider: oauthProviderSchema,
  stripe: stripeConfigSchema,
  root: configSchema,
};
